import {
    ACREDITACION_CONDICIONES_PRINT_BOX,
    ACREDITACION_ZONAS_OBSERVACIONES_PRINT_BOX
} from '@/composables/admin/events/acreditacionPrintBox';

/**
 * Política de impresión de las acreditaciones.
 *
 * Vive aquí, y no en el formulario, porque es una regla de negocio del dominio de
 * acreditaciones: qué campos se imprimen sobre un espacio FIJO de la plantilla y
 * cómo decidir si su contenido desborda. El componente sólo mide (delegando en
 * `QuillEditor`) y coordina la validación con la emisión del resultado.
 *
 * Lo que este módulo deliberadamente NO hace:
 * - No mide ni toca el DOM: el motor genérico `usePrintBoxFit` vive en el editor y
 *   entrega el resultado de ajuste del contenido actual (`flushPrintFit()`).
 * - No conoce los `ref` del formulario: recibe los resultados de ajuste indexados
 *   por campo, para no acoplar la política al contrato interno del editor.
 *
 * La geometría de las cajas (los números calibrados contra el PDF) está en
 * `acreditacionPrintBox.js`; cambiarla es otra responsabilidad.
 */

/**
 * Campos del formulario que se imprimen sobre una caja fija de la plantilla, en el
 * orden en que aparecen en el formulario, cada uno con su caja.
 *
 * `mensaje` no está: es el texto del correo al solicitante, no se imprime, así que
 * no tiene límite.
 *
 * `acceso_zonas` y `observaciones` comparten la caja corta (342 x 48, 3 líneas);
 * `condiciones` tiene la suya, más alta (342 x 211, 14 líneas).
 */
export const CAMPOS_CON_LIMITE = Object.freeze([
    Object.freeze({ campo: 'acceso_zonas', printBox: ACREDITACION_ZONAS_OBSERVACIONES_PRINT_BOX }),
    Object.freeze({ campo: 'observaciones', printBox: ACREDITACION_ZONAS_OBSERVACIONES_PRINT_BOX }),
    Object.freeze({ campo: 'condiciones', printBox: ACREDITACION_CONDICIONES_PRINT_BOX })
]);

/**
 * Caja de la plantilla que le toca a un campo.
 *
 * @param {string} campo
 * @returns {Object|null} la caja, o `null` si el campo no está sujeto a la plantilla
 */
export function printBoxParaCampo(campo) {
    const descriptor = CAMPOS_CON_LIMITE.find(item => item.campo === campo);

    return descriptor ? descriptor.printBox : null;
}

/**
 * Decide qué campos se salen de su caja a partir de los resultados de ajuste del
 * contenido actual (`flushPrintFit()`).
 *
 * Sólo bloquea un resultado **evaluable** que no entra. `evaluable: false` es la
 * apertura deliberada de `usePrintBoxFit` cuando no hay medición fiable, y un campo
 * sin resultado (por ejemplo un editor que no expone `flushPrintFit`) tampoco debe
 * bloquear: en ambos casos se deja pasar.
 *
 * @param {Object} [ajustesPorCampo] mapa `campo -> { evaluable, fits } | null`
 * @returns {string[]} campos cuyo contenido desborda, en orden de formulario
 */
export function camposDesbordados(ajustesPorCampo = {}) {
    return CAMPOS_CON_LIMITE
        .filter(({ campo }) => {
            const ajuste = ajustesPorCampo[campo];

            return Boolean(ajuste && ajuste.evaluable && !ajuste.fits);
        })
        .map(({ campo }) => campo);
}

/**
 * Composable de la política de impresión de acreditaciones.
 *
 * `hayContenidoQueNoEntra` recibe los resultados de ajuste del contenido actual; no
 * toca `ref` ni el DOM, así que el formulario puede recolectarlos como quiera sin
 * acoplar la decisión al contrato interno de los editores.
 *
 * @returns {{
 *   camposConLimite: ReadonlyArray<{ campo: string, printBox: Object }>,
 *   printBoxPara: (campo: string) => Object|null,
 *   camposDesbordados: (ajustesPorCampo?: Object) => string[],
 *   hayContenidoQueNoEntra: (ajustesPorCampo?: Object) => boolean
 * }}
 */
export function useAcreditacionPrintLimit() {
    return {
        camposConLimite: CAMPOS_CON_LIMITE,
        printBoxPara: printBoxParaCampo,
        camposDesbordados,
        hayContenidoQueNoEntra: ajustesPorCampo => camposDesbordados(ajustesPorCampo).length > 0
    };
}
